import React, { useId, useState } from 'react';
import { Alert, Button, InputNumber, Typography } from 'antd';
import { commasExact1Digit } from 'services/formatters';

import type useODAForm from './useODAForm';
import type { ODAField, ODALeg } from './useODAForm';
import './oda.css';

type Props = { form: ReturnType<typeof useODAForm> };

export const ODAFormFooter: React.FC<Props> = ({ form }) => (
  <div className="oda-form-footer">
    <Typography.Text type="secondary" className="oda-form-footer__note">
      Percentages measure loss (SL) or profit (TP) from open price.
    </Typography.Text>
    <Button type="primary" disabled={form.disabled} onClick={form.submit}>
      {form.actionLabel}
    </Button>
  </div>
);

const ODAPriceFields: React.FC<Props> = ({ form }) => {
  const prefix = useId().replace(/:/g, '');
  const [editing, setEditing] = useState<string | null>(null);
  const formatInput = (value: string, field: ODAField) => {
    if (!value.trim() || !Number.isFinite(Number(value))) return value;
    return (
      field === 'price'
        ? form.formatPrice(Number(value))
        : commasExact1Digit(value)
    ).replace(/,/g, '');
  };
  if (form.loading) {
    return (
      <Alert type="info" showIcon title="Loading the existing ODA orders…" />
    );
  }

  return (
    <div className="oda-price-fields">
      <dl className="oda-price-fields__reference" aria-label="Position prices">
        <dt>Open price</dt>
        <dd>
          {Number.isFinite(form.entry) ? form.formatPrice(form.entry) : '—'}
        </dd>
        <dt>Current {form.quoteName}</dt>
        <dd>
          {Number.isFinite(form.currentPrice)
            ? form.formatPrice(form.currentPrice)
            : '—'}
        </dd>
      </dl>
      {form.referenceError && (
        <Alert type="error" showIcon title={form.referenceError} />
      )}
      <div className="oda-price-fields__orders">
        <div
          className="oda-price-fields__row oda-price-fields__head"
          aria-hidden="true"
        >
          <span />
          <span>Price</span>
          <span>Percentage</span>
        </div>
        {(['stopLoss', 'takeProfit'] as ODALeg[]).map(leg => {
          const values = form[leg];
          const label = leg === 'stopLoss' ? 'Stop loss' : 'Take profit';
          const errorId = `${prefix}-${leg}-error`;
          const labelId = `${prefix}-${leg}-label`;
          const displayValue = (field: ODAField) => {
            // A placeholder keeps the dash out of the editable text,
            // so a correction never needs to delete it first.
            if (field === 'percentage' && values.percentageNegative) return '';
            const value =
              field === 'percentage'
                ? values.percentageInput
                : values.priceInput;
            return editing === `${form.ticketKey}-${leg}-${field}`
              ? value
              : formatInput(value, field);
          };
          return (
            <div
              key={leg}
              className="oda-price-fields__leg"
              role="group"
              aria-labelledby={labelId}
            >
              <div
                className="oda-price-fields__row"
                onChangeCapture={event => {
                  const target = event.target as HTMLInputElement;
                  if (
                    target.dataset.odaField === 'percentage' &&
                    /^\s*[-−]/.test(target.value)
                  ) {
                    // Capture on the parent so rejection precedes InputNumber's
                    // own handler, even when the accepted draft is empty.
                    event.stopPropagation();
                    target.value = displayValue('percentage');
                  }
                }}
              >
                <span className="oda-price-fields__label" id={labelId}>
                  {leg === 'stopLoss' ? 'Stop Loss' : 'Take Profit'}
                </span>
                {(['price', 'percentage'] as ODAField[]).map(field => {
                  const isPercentage = field === 'percentage';
                  const negative = isPercentage && values.percentageNegative;
                  const value = isPercentage
                    ? values.percentageInput
                    : values.priceInput;
                  const editingKey = `${form.ticketKey}-${leg}-${field}`;
                  return (
                    <InputNumber
                      key={field}
                      className={`oda-price-fields__input${negative ? ' oda-price-fields__input--negative' : ''}`}
                      stringMode
                      data-oda-field={field}
                      aria-label={`${label} ${field}`}
                      aria-invalid={Boolean(values.error)}
                      aria-describedby={values.error ? errorId : undefined}
                      aria-valuetext={
                        negative ? 'Invalid percentage' : undefined
                      }
                      value={value}
                      formatter={() => displayValue(field)}
                      placeholder={negative ? '—' : undefined}
                      suffix={isPercentage && !negative ? '%' : undefined}
                      min={isPercentage ? '0' : undefined}
                      step={isPercentage ? 0.1 : 1}
                      disabled={isPercentage && form.percentageDisabled}
                      status={values.error ? 'error' : undefined}
                      changeOnBlur={false}
                      parser={text => text ?? ''}
                      // Commit only user edits. Formatting, focus, blur and Enter
                      // must not round a saved trigger or recalculate it from the
                      // rounded percentage. Invalid text still blocks submission.
                      onInput={text => {
                        setEditing(editingKey);
                        form.change(leg, field, text);
                      }}
                      onStep={(value, { type }) => {
                        if (negative) {
                          if (type === 'down') return;
                          value = '0';
                        }
                        setEditing(null);
                        form.change(leg, field, value);
                      }}
                      onBlur={() => setEditing(null)}
                      onPressEnter={() => setEditing(null)}
                    />
                  );
                })}
              </div>
              {values.error && (
                <Typography.Text
                  type="danger"
                  role="alert"
                  id={errorId}
                  className="oda-price-fields__error"
                >
                  {values.error}
                </Typography.Text>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ODAPriceFields;
